import { IRankingOrganisationSearchFilters } from '@studyportals/ranking-api-interface';
import { SearchPageFilterStateChangedEvent } from '@studyportals/search-page-interface';
import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { FilterKey } from '@studyportals/search-filters';
import { countryGetIsoCode } from '@studyportals/static-domain-data/countries/country-get-iso-code';
import { disciplineGetSubdisciplines } from '@studyportals/static-domain-data/disciplines/discipline-get-subdisciplines';
import { DeliveryMethodEnum, IOrganisationSearchFilters } from '@studyportals/search-api-interface';

import { SearchOrganisationPresenter } from '@/presenters/SearchOrganisationPresenter';
import { EventAggregationService } from '@/platform/EventAggregationService';
import { SearchRankingFiltersMapper } from '@/platform/SearchRankingFiltersMapper';
import { ConvergingTask } from './ConvergingTask';
import { EmbeddedStateCommittedEvent } from '@/url/events/EmbeddedStateCommittedEvent';
import { injectable } from 'inversify';
import { Store } from '@/store/Store';
import { OrganisationSearchResult } from '@/presenters/OrganisationSearchResult';
import { ApplicationStatusReadyEvent } from './events/ApplicationStatusReadyEvent';
import { FilterSelection } from '@/filters/selection/FilterSelection';
import { AttendanceOptions } from '@/filters/options/AttendanceOptions';
import { PaginationDefinition } from '@/store/PaginationDefinition';
import { RankingOrganisationPresenter } from '@/presenters/RankingOrganisationPresenter';
import { SortingSelection } from '@/sorting/SortingSelection';
import { SortingOptions } from '@/sorting/SortingOptions';


@injectable()
export class Search
	extends ConvergingTask<OrganisationSearchResult>
	implements ISubscriber<ApplicationStatusReadyEvent>, ISubscriber<EmbeddedStateCommittedEvent> {
	private readonly store: Store;
	private readonly filterSelection: FilterSelection;
	private readonly sortingSelection: SortingSelection;
	private readonly eventAggregationService: EventAggregationService;
	private readonly searchOrganisationPresenter: SearchOrganisationPresenter;
	private readonly rankingOrganisationPresenter: RankingOrganisationPresenter;

	private lastHandledSequence = -1;
	private canSearch = false;

	public constructor(
		store: Store,
		eventAggregationService: EventAggregationService,
		searchOrganisationPresenter: SearchOrganisationPresenter,
		rankingOrganisationPresenter: RankingOrganisationPresenter,
		filterSelection: FilterSelection,
		sortingSelection: SortingSelection
	) {
		super();

		this.store = store;
		this.eventAggregationService = eventAggregationService;
		this.searchOrganisationPresenter = searchOrganisationPresenter;
		this.rankingOrganisationPresenter = rankingOrganisationPresenter;
		this.filterSelection = filterSelection;
		this.sortingSelection = sortingSelection;

		this.eventAggregationService.subscribeTo(ApplicationStatusReadyEvent.EventType, this);
		this.eventAggregationService.subscribeTo(EmbeddedStateCommittedEvent.EventType, this);
	}

	public async notify(
		event: ApplicationStatusReadyEvent | EmbeddedStateCommittedEvent,
		sequence: number
	): Promise<void> {
		if (this.isEarlierEvent(sequence)) {
			return;
		}

		if (ApplicationStatusReadyEvent.is(event)) {
			this.canSearch = true;
		}

		await this.search();
	}

	private isEarlierEvent(receivedSequence: number): boolean {
		const result = receivedSequence < this.lastHandledSequence;

		this.lastHandledSequence = receivedSequence;
		return result;
	}

	public async search(): Promise<void> {
		await this.execute(null);
	}

	protected override async run(): Promise<OrganisationSearchResult> {
		this.store.actions.clearSearch();
		window.scrollTo(0, 0);

		return await this.getOrganisationSearchResults();
	}

	protected override commit(result: OrganisationSearchResult): void {
		this.store.actions.commitSearch(result.organisations, result.totalHits);
	}

	protected override handleError(): void {
		this.store.actions.clearSearch();
	}

	protected override afterEachRun(): void {
		this.eventAggregationService.publishTo(
			SearchPageFilterStateChangedEvent.EventType,
			new SearchPageFilterStateChangedEvent()
		);
	}

	protected override canExecute(): boolean {
		return this.canSearch;
	}

	private determineFiltersFromState(): IOrganisationSearchFilters {
		const state = this.filterSelection.state.reactive;

		return {
			organisationId: state.isAnySelectedFor(FilterKey.ORGANISATIONS)
				? state.getFor(FilterKey.ORGANISATIONS)[0]
				: undefined,
			degree: this.store.getters.getDegree().toEnum(),
			disciplines: state.isAnySelectedFor(FilterKey.DISCIPLINES) ? this.determineDisciplinesFromState() : undefined,
			deliveryMethods: state.isAnySelectedFor(FilterKey.DELIVERY_METHOD)
				? this.determineDeliveryMethodsFromState()
				: undefined,
			countryIsoCodes: state.isAnySelectedFor(FilterKey.COUNTRY) ? this.determineCountryIsoCodesFromState() : undefined,
			regionIds: state.isAnySelectedFor(FilterKey.CONTINENT) ? [...state.getFor(FilterKey.CONTINENT)] : undefined,
			areaIds: state.isAnySelectedFor(FilterKey.AREA) ? [...state.getFor(FilterKey.AREA)] : undefined,
		};
	}

	private determineFiltersForRankings(): IRankingOrganisationSearchFilters {
		const filtersFromState = this.determineFiltersFromState();

		const state = this.filterSelection.state.reactive;

		const filtersForRankings = SearchRankingFiltersMapper.map({
			...filtersFromState,
			disciplines: filtersFromState.disciplines && state.getFor(FilterKey.DISCIPLINES) as string[]
		});

		return filtersForRankings;
	}

	private determineCountryIsoCodesFromState(): string[] {
		const countryIsoCodes: string[] = [];
		const state = this.filterSelection.state.reactive;

		[...state.getFor(FilterKey.COUNTRY)].map((c) => {
			const countrIsoCode = countryGetIsoCode(c);
			if (countrIsoCode !== null) {
				countryIsoCodes.push(countrIsoCode.toLowerCase());
			}
		});

		return countryIsoCodes;
	}

	private determineDeliveryMethodsFromState(): DeliveryMethodEnum[] {
		const state = this.filterSelection.state.reactive;

		return [...state.getFor(FilterKey.DELIVERY_METHOD)].map((val) => AttendanceOptions.toEnum(val));
	}

	private determineDisciplinesFromState(): string[] {
		const disciplines: string[] = [];
		const state = this.filterSelection.state.reactive;

		const selectedDisciplines = state.getFor(FilterKey.DISCIPLINES);

		selectedDisciplines.forEach((discipline) => {
			disciplines.push(discipline);
			const subdisciplines = disciplineGetSubdisciplines(discipline).map((subdiscipline) => subdiscipline.id);
			disciplines.push(...subdisciplines);
		});

		return disciplines;
	}

	private determineResultOffset(): number {
		return (this.store.getters.getCurrentPageNumber() - 1) * PaginationDefinition.RESULTS_PER_PAGE;
	}

	private async getOrganisationSearchResults(): Promise<OrganisationSearchResult> {

		const sortByRankings = this.sortingSelection.isSelected(SortingOptions.UNIVERSITY_META_RANKING);

		if (sortByRankings) {
			return await this.rankingOrganisationPresenter.search(
				this.determineFiltersForRankings(),
				PaginationDefinition.RESULTS_PER_PAGE,
				this.determineResultOffset()
			);
		}

		return await this.searchOrganisationPresenter.search(
			this.determineFiltersFromState(),
			PaginationDefinition.RESULTS_PER_PAGE,
			this.determineResultOffset()
		);
	}
}
